import { Navigate, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import AdminPortalNav from './AdminPortalNav'

// Reuse the same query keys as useModerator/useAdmin so layout shares cache with Header.
// This avoids redirect-to-home when the layout refetch fails or returns different data.
function useAdminPortalAccess() {
  const { user } = useAuth()

  const { data: isModerator, isLoading: moderatorLoading } = useQuery({
    queryKey: ['is-moderator', user?.id],
    queryFn: async () => {
      if (!user) return false
      const { data, error } = await supabase
        .from('profiles')
        .select('role, user_type')
        .eq('id', user.id)
        .maybeSingle()
      if (error || !data) return false
      const role = data?.role
      const userType = data?.user_type
      return (
        role === 'moderator' ||
        role === 'admin' ||
        userType === 'moderator' ||
        userType === 'admin'
      )
    },
    enabled: !!user,
    staleTime: 60000,
  })

  const { data: isAdmin, isLoading: adminLoading } = useQuery({
    queryKey: ['is-admin', user?.id],
    queryFn: async () => {
      if (!user) return false
      const { data, error } = await supabase
        .from('profiles')
        .select('role, user_type')
        .eq('id', user.id)
        .maybeSingle()
      if (error || !data) return false
      const role = data?.role
      const userType = data?.user_type
      return role === 'admin' || userType === 'admin'
    },
    enabled: !!user,
    staleTime: 60000,
  })

  const loading = moderatorLoading || adminLoading
  const canAccess = isModerator ?? false
  const admin = isAdmin ?? false
  return { canAccess, isAdmin: admin, loading }
}

/**
 * Layout for all /admin/* routes. Restricts access to admin or moderator only;
 * redirects others to Home. Provides skip link, portal nav, and main content area.
 * Uses same role query keys as useModerator/useAdmin so access is consistent with the Header link.
 */
export default function AdminPortalLayout() {
  const { canAccess, isAdmin, loading } = useAdminPortalAccess()

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-8" role="status" aria-live="polite">
        Loading…
      </div>
    )
  }

  if (!canAccess) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#admin-main" className="skip-link">
        Skip to main content
      </a>
      <div className="flex flex-1">
        <aside
          className="w-56 shrink-0 border-r-2 p-4"
          style={{ borderColor: 'var(--bg-tertiary)' }}
          aria-label="Admin portal sections"
        >
          <AdminPortalNav isAdmin={isAdmin} />
        </aside>
        <main
          id="admin-main"
          className="flex-1 container mx-auto px-4 py-8"
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}
